import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import type { AttentionItem } from "../dashboard-selectors";
import { getTransferStats } from "../dashboard-selectors";

export function OperationalInsight({
  attentionItems,
  pendingCount,
  alertCount,
  providerDegradedCount,
  topChain,
}: {
  attentionItems: AttentionItem[];
  pendingCount: number;
  alertCount: number;
  providerDegradedCount: number;
  topChain: string | null;
}) {
  const { total30d, total30dHint } = getTransferStats();

  const healthy = attentionItems.length === 0;
  if (healthy) {
    return (
      <div className="flex items-center justify-between rounded-xl border border-success-border bg-success-soft px-4 py-3 text-sm text-success">
        <div className="flex items-center gap-2">
          <Sparkles className="size-4" />
          <span>All operational indicators are healthy.</span>
        </div>
        <Link to="/monitoring" className="text-xs font-semibold hover:underline">
          View monitoring →
        </Link>
      </div>
    );
  }

  const insightParts: string[] = [];
  if (total30d && total30dHint) {
    insightParts.push(`${total30d} transfers in the last 30 days (${total30dHint})`);
  }
  if (topChain) {
    insightParts.push(`${topChain} drives the largest AUM share`);
  }
  if (pendingCount > 0) {
    insightParts.push(`${pendingCount} approval${pendingCount === 1 ? "" : "s"} pending`);
  }
  if (providerDegradedCount > 0) {
    insightParts.push(`${providerDegradedCount} provider${providerDegradedCount === 1 ? "" : "s"} degraded`);
  }
  if (alertCount > 0) {
    insightParts.push(`${alertCount} active alert${alertCount === 1 ? "" : "s"}`);
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line bg-surface px-4 py-3">
      <div className="flex items-center gap-2 text-sm text-ink">
        <Sparkles className="size-4 text-primary" />
        <span className="font-semibold">Operational insight:</span>
        <span className="text-muted">{insightParts.join(" · ")}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {attentionItems.slice(0, 4).map((item) => (
          <Link
            key={item.text}
            to={item.href}
            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
              item.tone === "danger"
                ? "bg-danger-soft text-danger"
                : item.tone === "warning"
                ? "bg-warning-soft text-warning"
                : "bg-info-soft text-info"
            }`}
          >
            {item.text}
          </Link>
        ))}
      </div>
    </div>
  );
}
